import { useEffect, useRef } from 'react'

import { noop } from '../utils/function'

interface SwipeDetector {
  onLeftSwiped?: () => void
  onRightSwiped?: () => void
  onUpSwiped?: () => void
  onDownSwiped?: () => void
  domRef: React.MutableRefObject<HTMLDivElement | null>
  horizontalSwipeThreshold?: number
  verticalSwipeThreshold?: number
}

export const useSwipeDetector = ({
  onLeftSwiped = noop,
  onRightSwiped = noop,
  domRef,
  onDownSwiped = noop,
  onUpSwiped = noop,
  horizontalSwipeThreshold = 100,
  verticalSwipeThreshold = 100,
}: SwipeDetector): void => {
  const xDown = useRef<number | null>(null)
  const yDown = useRef<number | null>(null)

  useEffect(() => {
    const domNode = domRef?.current

    if (domNode === null || domNode === undefined) {
      return
    }

    const handleTouchStart = (evt: TouchEvent): void => {
      const firstTouch = evt.touches[0]
      xDown.current = firstTouch?.clientX ?? 0
      yDown.current = firstTouch?.clientY ?? 0
    }

    const handleTouchEnd = (evt: TouchEvent): void => {
      if (xDown.current === null || yDown.current === null) {
        return
      }

      const firstTouch = evt.changedTouches[0]
      const xUp = firstTouch?.clientX ?? 0
      const yUp = firstTouch?.clientY ?? 0
      const xDiff = xDown.current - xUp
      const yDiff = yDown.current - yUp

      if (Math.abs(xDiff) > Math.abs(yDiff)) {
        if (xDiff > horizontalSwipeThreshold) {
          onLeftSwiped()
        } else if (xDiff < -horizontalSwipeThreshold) {
          onRightSwiped()
        }
      } else {
        if (yDiff > verticalSwipeThreshold) {
          onUpSwiped()
        } else if (yDiff < -verticalSwipeThreshold) {
          onDownSwiped()
        }
      }

      xDown.current = null
      yDown.current = null
    }

    domNode.addEventListener('touchstart', handleTouchStart, { passive: false })
    domNode.addEventListener('touchend', handleTouchEnd, { passive: false })

    return () => {
      domNode.removeEventListener('touchstart', handleTouchStart)
      domNode.removeEventListener('touchend', handleTouchEnd)
    }
  }, [domRef, onLeftSwiped, onRightSwiped, onUpSwiped, onDownSwiped, verticalSwipeThreshold, horizontalSwipeThreshold])
}
